import React, { useEffect } from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';
import { Divider, Grid, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { Btn, BtnBase, InputController, RadioGroupController, setProgress } from 'Components';
import Yup from 'Utils/Yup';
import { mapValidationErrors } from 'Utils/Http/MapValidationErrors';
import { CERTIFICATE_CATEGORIES, NO_CERTIFICATE_CATEGORY } from 'Constants/CertificateNames';
import { createCertificateName, updateCertificateName } from 'Services/CertificateNamesSvc';

const SAVE_CERTIFICATE_NAME_BTN = 'SAVE_CERTIFICATE_NAME_BTN';

const NAME_MAX_LENGTH = 255;

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '560px',
    borderRadius: '20px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const useStyle = makeStyles((theme) => ({
    title: {
        fontSize: '23px',
        color: 'rgba(0, 0, 0, 1)',
        fontWeight: '500',
    },
}));

// The rules that matter - a duplicate spelling, a name with no letters in it -
// are enforced by the API and come back as errors.name, which this form drops
// onto the field. Yup only catches what can be caught without a round trip.
//
// The max length is a .test() with a bare message key, not Yup max():
// Helpers/TranslateRHFErrorMsg (what every *Controller uses) understands string
// messages only, while Utils/Yup makes max() produce a {key, values} object,
// which would render as "validation.[object Object]".
const schema = Yup.object({
    name: Yup.string()
        .trim()
        .required()
        .test(
            'certificateNameTooLong',
            'certificateNameTooLong',
            (value) => !value || value.length <= NAME_MAX_LENGTH,
        ),
    category: Yup.string().nullable(),
});

/**
 * Create / edit dialog for one approved certificate name.
 *
 * `listId` is the certificate name list a new name goes into.
 *
 * A null `record` means create, otherwise it is the row being edited - the list
 * already holds every field, so there is no detail GET to wait for.
 */
const CertificateNameFormModal = ({ open, record, listId, onClose, onSaved }) => {
    const classes = useStyle();
    const { t } = useTranslation();

    const form = useForm({
        resolver: yupResolver(schema),
        defaultValues: { name: '', category: NO_CERTIFICATE_CATEGORY },
    });
    const { control, handleSubmit, reset, setError } = form;

    // Re-seeding on every open is also what clears the 422 field errors and the
    // text left behind the previous time the dialog was used.
    useEffect(() => {
        if (!open) {
            return;
        }

        reset({
            name: record?.name ?? '',
            category: record?.category ?? NO_CERTIFICATE_CATEGORY,
        });
    }, [open, record, reset]);

    const categoryOptions = [
        { value: NO_CERTIFICATE_CATEGORY, label: t('admin.certificateNames.noCategory') },
        ...CERTIFICATE_CATEGORIES.map((value) => ({
            value,
            label: t(`admin.certificateNames.categories.${value}`),
        })),
    ];

    const handleFormSubmit = async (values) => {
        setProgress(SAVE_CERTIFICATE_NAME_BTN, true);
        try {
            if (record) {
                await updateCertificateName(record.id, values);
                toast.success(t('toast.success.updatedSuccessfully'));
            } else {
                // Into the list the page has selected. A duplicate is judged
                // within that list only, and comes back as errors.name.
                await createCertificateName(values, listId);
                toast.success(t('toast.success.addedSuccessfully'));
            }
            onSaved();
        } catch (error) {
            const fieldErrors = error?.response?.data?.errors;

            if (error?.response?.status === 422 && fieldErrors) {
                // Duplicates and letter-less names come back as errors.name, so
                // they belong under the input rather than in a toast that does
                // not say which field is wrong. mapValidationErrors marks them
                // serverSideError, which passes the API message through as-is.
                mapValidationErrors(fieldErrors).forEach(({ name, message, type }) => {
                    setError(name, { type, message });
                });
            } else {
                toast.error(error?.response?.data?.message);
            }
        }
        setProgress(SAVE_CERTIFICATE_NAME_BTN, false);
    };

    return (
        <Modal
            aria-labelledby="certificate-name-modal-title"
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <Box sx={style}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <h3 id="certificate-name-modal-title" className={classes.title}>
                                {record
                                    ? t('admin.certificateNames.editTitle')
                                    : t('admin.certificateNames.addTitle')}
                            </h3>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Divider style={{ margin: '10px 0', backgroundColor: 'rgba(0, 0, 0, 0.2)' }} />

                    <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
                        <Grid container style={{ marginTop: '10px' }} spacing={3}>
                            <Grid item xs={12}>
                                <InputController
                                    control={control}
                                    name="name"
                                    label={t('admin.certificateNames.name')}
                                    placeholder={t('admin.certificateNames.namePlaceholder')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RadioGroupController
                                    control={control}
                                    name="category"
                                    label={t('admin.certificateNames.category')}
                                    defaultValue={NO_CERTIFICATE_CATEGORY}
                                    options={categoryOptions}
                                    row
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            style={{ marginTop: '10px' }}
                            spacing={2}
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Grid item>
                                {/* The Cancel button in PasswordChangeModal has no
                                    onClick and does nothing - this one closes. */}
                                <BtnBase variant="text" fullWidth={false} onClick={onClose}>
                                    {t('common.cancel')}
                                </BtnBase>
                            </Grid>
                            <Grid item>
                                <Btn fullWidth={false} type="submit" name={SAVE_CERTIFICATE_NAME_BTN}>
                                    {record ? t('common.update') : t('common.create')}
                                </Btn>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Fade>
        </Modal>
    );
};

export default CertificateNameFormModal;
